import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { api } from "../utils/api";

export function getStatusColor(status) {
  switch (status) {
    case "active":
      return "success";
    case "not_found":
      return "warning";
    case "error":
      return "error";
    default:
      return "default";
  }
}

export function getStatusLabel(status) {
  switch (status) {
    case "active":
      return "Active";
    case "not_found":
      return "Not Found";
    case "error":
      return "Error";
    default:
      return status;
  }
}

export function useProductManagement() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEshop, setSelectedEshop] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedManufacturer, setSelectedManufacturer] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProductNumber, setSelectedProductNumber] = useState("");
  const [selectedSku, setSelectedSku] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [tableResetKey, setTableResetKey] = useState(0); // Used to reset SortableTable selection
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteSnackbar, setDeleteSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [syncSnackbar, setSyncSnackbar] = useState({ open: false, message: "", severity: "info" });

  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const queryClient = useQueryClient();

  const {
    data: productsData,
    isLoading: productsLoading,
    error: productsError,
    refetch: refetchProducts,
  } = useQuery(
    ["products", page, rowsPerPage, searchTerm, selectedEshop, selectedStatus, selectedManufacturer, selectedCategory, selectedProductNumber, selectedSku, sortBy, sortOrder],
    () => {
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        search: searchTerm,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      if (selectedEshop) params.eshop_id = selectedEshop;
      if (selectedStatus) params.status = selectedStatus;
      if (selectedManufacturer) params.manufacturer = selectedManufacturer;
      if (selectedCategory) params.category = selectedCategory;
      if (selectedProductNumber) params.product_number = selectedProductNumber;
      if (selectedSku) params.model_sku = selectedSku;

      return api.get("/api/products", { params }).then((res) => res.data);
    },
    { keepPreviousData: true }
  );

  const { data: eshops } = useQuery("eshops", () => api.get("/api/eshops").then((res) => res.data));
  const { data: manufacturers = [] } = useQuery("manufacturers", () => api.get("/api/manufacturers").then((res) => res.data));
  const { data: categories = [] } = useQuery("categories", () => api.get("/api/categories").then((res) => res.data));

  const exportProductsMutation = useMutation(
    (format) => api.get("/api/products/export", { responseType: "blob", params: { format } }),
    {
      onSuccess: (response, format) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        const timestamp = new Date().toISOString().split("T")[0];
        const extension = format === "xlsx" ? "xlsx" : "csv";
        link.setAttribute("download", `products_export_${timestamp}.${extension}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      },
    }
  );

  const syncSheetsMutation = useMutation(() => api.post("/api/sheets/sync"), {
    onSuccess: () => {
      setSyncSnackbar({ open: true, message: "Google Sheets sync started successfully!", severity: "success" });
    },
    onError: () => {
      setSyncSnackbar({ open: true, message: "Failed to start Google Sheets sync.", severity: "error" });
    },
  });

  const handleCloseSyncSnackbar = () => {
    setSyncSnackbar((prev) => ({ ...prev, open: false }));
  };

  const deleteProductsMutation = useMutation(
    (productIds) => api.post('/api/products/batch-delete', { product_ids: productIds }),
    {
      onSuccess: (response, productIds) => {
        queryClient.invalidateQueries([
          "products",
          page,
          rowsPerPage,
          searchTerm,
          selectedEshop,
          selectedStatus,
          selectedManufacturer,
          selectedCategory,
        ]);
        setTableResetKey((prev) => prev + 1);
        const deletedCount = response.data.deleted_count || productIds.length;
        setDeleteSnackbar({ open: true, message: `${deletedCount} product(s) deleted successfully`, severity: "success" });
        setSelectedProductIds(new Set());
      },
      onError: (error) => {
        setDeleteSnackbar({ open: true, message: `Error deleting products: ${error.message}`, severity: "error" });
      },
    }
  );

  const handleSelectionChange = (selectedIds) => {
    setSelectedProductIds(new Set(selectedIds));
  };

  const handleOpenDeleteDialog = () => setIsDeleteDialogOpen(true);
  const handleCloseDeleteDialog = () => setIsDeleteDialogOpen(false);

  const handleConfirmDelete = () => {
    deleteProductsMutation.mutate(Array.from(selectedProductIds));
    setIsDeleteDialogOpen(false);
  };

  const handleCloseDeleteSnackbar = () => {
    setDeleteSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleSortChange = (property, order) => {
    setSortBy(property);
    setSortOrder(order);
    setPage(0); // Reset to first page when sorting changes
  };

  const handleExportCSV = () => exportProductsMutation.mutate("csv");
  const handleExportXLSX = () => exportProductsMutation.mutate("xlsx");
  const handleSyncSheets = () => syncSheetsMutation.mutate();

  const handleOpenModal = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
    setIsModalOpen(false);
  };

  // Filter setters that also reset to page 0 (used directly by the filter Selects/TextFields)
  const setSelectedProductNumberAndResetPage = (value) => {
    setSelectedProductNumber(value);
    setPage(0);
  };
  const setSelectedSkuAndResetPage = (value) => {
    setSelectedSku(value);
    setPage(0);
  };
  const setSelectedEshopAndResetPage = (value) => {
    setSelectedEshop(value);
    setPage(0);
  };
  const setSelectedStatusAndResetPage = (value) => {
    setSelectedStatus(value);
    setPage(0);
  };
  const setSelectedManufacturerAndResetPage = (value) => {
    setSelectedManufacturer(value);
    setPage(0);
  };
  const setSelectedCategoryAndResetPage = (value) => {
    setSelectedCategory(value);
    setPage(0);
  };

  return {
    // Query data
    productsData,
    productsLoading,
    productsError,
    refetchProducts,
    eshops,
    manufacturers,
    categories,
    // Pagination / sorting
    page,
    rowsPerPage,
    sortBy,
    sortOrder,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSortChange,
    // Filters
    searchTerm,
    handleSearch,
    selectedEshop,
    setSelectedEshop: setSelectedEshopAndResetPage,
    selectedStatus,
    setSelectedStatus: setSelectedStatusAndResetPage,
    selectedManufacturer,
    setSelectedManufacturer: setSelectedManufacturerAndResetPage,
    selectedCategory,
    setSelectedCategory: setSelectedCategoryAndResetPage,
    selectedProductNumber,
    setSelectedProductNumber: setSelectedProductNumberAndResetPage,
    selectedSku,
    setSelectedSku: setSelectedSkuAndResetPage,
    // Selection / table reset
    selectedProductIds,
    handleSelectionChange,
    tableResetKey,
    // Modal
    selectedProduct,
    isModalOpen,
    handleOpenModal,
    handleCloseModal,
    // Export / sync
    exportProductsMutation,
    syncSheetsMutation,
    syncSnackbar,
    handleCloseSyncSnackbar,
    handleExportCSV,
    handleExportXLSX,
    handleSyncSheets,
    // Delete
    deleteProductsMutation,
    isDeleteDialogOpen,
    handleOpenDeleteDialog,
    handleCloseDeleteDialog,
    handleConfirmDelete,
    deleteSnackbar,
    handleCloseDeleteSnackbar,
  };
}
